import * as React from 'react';
import { gql } from '@apollo/client';
import { OperationResult } from '@bondvet/types';
import isEmail from 'validator/lib/isEmail';
import client from 'lib/apollo/client';
import { GRAPHQL_CLIENT_NAMES } from 'lib/types';
import useTrackEmailSignupForCareers from 'analytics/ttd/hooks/useTrackEmailSignupForCareers';
import useGoogleAnalytics from 'analytics/hooks/useGoogleAnalytics';

const mutation = gql`
    mutation subscribeCareersNewsletter($email: String!) {
        addMailToCareersList(email: $email) {
            success
            error
        }
    }
`;

type Status =
    | 'notSubmitted'
    | 'invalidEmail'
    | 'submitting'
    | 'success'
    | 'failed';

export interface UseNewsletterSignupForCareers {
    handleSubmit: (event: React.ChangeEvent<HTMLFormElement>) => Promise<void>;
    handleChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
    email: string;
    isValidEmail: boolean;
    status: Status;
    success: boolean;
}

interface CareersSignupVariables {
    email: string;
}

interface CareersSignupResult {
    addMailToCareersList: OperationResult;
}

export default function useNewsletterSignupForCareers(): UseNewsletterSignupForCareers {
    const [email, setEmail] = React.useState('');
    const [status, setStatus] = React.useState<Status>('notSubmitted');
    const trackEmailSignupForCareers = useTrackEmailSignupForCareers();
    const { trackWithAnyGoogleAnalytics } = useGoogleAnalytics();

    const isValidEmail = React.useMemo(() => {
        return isEmail(email);
    }, [email]);

    const handleSubmit = React.useCallback(
        (event: React.ChangeEvent<HTMLFormElement>) => {
            event.preventDefault();

            trackEmailSignupForCareers();

            async function submit() {
                setStatus('submitting');

                try {
                    const result = await client.mutate<
                        CareersSignupResult,
                        CareersSignupVariables
                    >({
                        mutation,
                        context: {
                            clientName: GRAPHQL_CLIENT_NAMES.newsletter,
                        },
                        variables: { email },
                    });

                    if (!result.data?.addMailToCareersList?.success) {
                        console.error(
                            'addMailToCareersList error: ',
                            result.data?.addMailToCareersList?.error,
                        );
                        setStatus('failed');
                        return;
                    }

                    setEmail('');
                    setStatus('success');

                    trackWithAnyGoogleAnalytics(
                        false,
                        'careers_newsletter_subscription',
                        email,
                    );
                } catch (error) {
                    console.error('addMailToCareersList error: ', error);
                    setStatus('failed');
                }
            }

            if (status === 'submitting' || status === 'success') {
                return Promise.resolve();
            }

            if (!isValidEmail) {
                setStatus('invalidEmail');
                return Promise.resolve();
            }

            return submit();
        },
        [
            status,
            isValidEmail,
            email,
            trackEmailSignupForCareers,
            trackWithAnyGoogleAnalytics,
        ],
    );

    const handleChangeEmail = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
        },
        [],
    );

    return {
        handleSubmit,
        handleChangeEmail,
        email,
        isValidEmail,
        status,
        success: status === 'success',
    };
}
